'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { buildRoomSequence, ACTIONS, type Room } from './gameData';
import CobwebSvg from './CobwebSvg';
import BloodDrips from './BloodDrips';
import JumpscareOverlay from './JumpscareOverlay';
import VictoryScreen from './VictoryScreen';

type GamePhase = 'doors' | 'zoom' | 'room' | 'jumpscare' | 'victory';

interface GameState {
  phase: GamePhase;
  currentRoomIndex: number;
  selectedDoor: 1 | 2 | null;
  shakeScreen: boolean;
  zoomingDoor: 1 | 2 | null;
  rooms: Room[];
}

interface GameScreenProps {
  onRestart: () => void;
}

// Creepy door inscriptions — 20 unique texts, each level gets a unique pair (no repeats within same level)
const DOOR_INSCRIPTIONS = [
  'BĖK!',
  'NEIŠLEISK JŲ Į LAUKĄ.',
  'MELDŽIAME, NEATIDARYKITE.',
  'UŽ ŠIŲ DURŲ TAVO BAIMĖ JAU LAUKIA.',
  'JIE GAUDEO KIEKVIENĄ TAVO ŽINGSNĮ.',
  'ATIDARYK, JEIGU ESI KALTAS.',
  'KAIRIOSIOS IŠGELBĖS KŪNĄ. DEŠINIOSIOS – SIELĄ.',
  'UŽĖJĘS ČIA, TURĖSI PALIKTI DRAUGĄ.',
  'TIK NEBIJANTYS TAMSOS RAS KELIĄ.',
  'ČIA PRASIDEDA TAVO KOŠMARAS.',
  'MES ŽINOME, KĄ PADAREI PRAEITĄ NAKTĮ.',
  'NIEKAS IŠ ŠIO KAMBARIO NEIŠĖJO GYVAS.',
  'TAVO LAIKAS BAIGIASI... 5... 4... 3...',
  'PAŽVELK PRO AKUTĘ. JIE STEBI.',
  'ŠŠŠ... NESUKELK TRIUKŠMO. JIE MIEGA.',
  'AR GIRDI JŲ KVĖPAVIMĄ UŽ DURŲ?',
  'TU ESI SEKANTIS.',
  'ĮĖJIMAS NEMOKAMAS. IŠĖJIMAS – VISKĄ.',
  'SVEIKI ATVYKĘ. ATGAL KELIO NĖRA.',
  'NIEKAS TAVĘS NEGIRDĖS.',
];

// Door color themes — occasionally vary, mostly dark red/blue, rarely other creepy colors
const DOOR_COLOR_THEMES = [
  // Default: red left, blue right
  { left: { frame: 'linear-gradient(135deg, #5a0a0a 0%, #3d0606 50%, #2a0404 100%)', border: '#7a1010', borderBottom: '#5a0808', surface: 'linear-gradient(170deg, #4a0808 0%, #350606 30%, #280404 70%, #300606 100%)', panel: '#3d0606', panelBorder: '#5a0808', panelShadow: 'rgba(80,10,10,0.3)', hover: 'rgba(120,10,10,0.15)', hoverShadow: 'rgba(180,20,20,0.3)', shadow: '4px 0 16px rgba(0,0,0,0.9), -2px 0 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(0,0,0,0.5)' },
    right: { frame: 'linear-gradient(135deg, #0a1a3d 0%, #060d2a 50%, #040818 100%)', border: '#102060', borderBottom: '#0a1848', surface: 'linear-gradient(170deg, #0d1e4a 0%, #081430 30%, #060e22 70%, #081430 100%)', panel: '#0d1e4a', panelBorder: '#102060', panelShadow: 'rgba(10,20,80,0.3)', hover: 'rgba(10,20,120,0.15)', hoverShadow: 'rgba(20,40,180,0.3)', shadow: '-4px 0 16px rgba(0,0,0,0.9), 2px 0 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(0,0,0,0.5)' } },
  // Both red
  { left: { frame: 'linear-gradient(135deg, #5a0a0a 0%, #3d0606 50%, #2a0404 100%)', border: '#7a1010', borderBottom: '#5a0808', surface: 'linear-gradient(170deg, #4a0808 0%, #350606 30%, #280404 70%, #300606 100%)', panel: '#3d0606', panelBorder: '#5a0808', panelShadow: 'rgba(80,10,10,0.3)', hover: 'rgba(120,10,10,0.15)', hoverShadow: 'rgba(180,20,20,0.3)', shadow: '4px 0 16px rgba(0,0,0,0.9), -2px 0 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(0,0,0,0.5)' },
    right: { frame: 'linear-gradient(135deg, #5a0a0a 0%, #3d0606 50%, #2a0404 100%)', border: '#7a1010', borderBottom: '#5a0808', surface: 'linear-gradient(170deg, #4a0808 0%, #350606 30%, #280404 70%, #300606 100%)', panel: '#3d0606', panelBorder: '#5a0808', panelShadow: 'rgba(80,10,10,0.3)', hover: 'rgba(120,10,10,0.15)', hoverShadow: 'rgba(180,20,20,0.3)', shadow: '-4px 0 16px rgba(0,0,0,0.9), 2px 0 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(0,0,0,0.5)' } },
  // Left yellow, right dark
  { left: { frame: 'linear-gradient(135deg, #4a3a00 0%, #332800 50%, #221a00 100%)', border: '#6a5200', borderBottom: '#4a3800', surface: 'linear-gradient(170deg, #3d3000 0%, #2a2000 30%, #1e1600 70%, #2a2000 100%)', panel: '#3d3000', panelBorder: '#5a4800', panelShadow: 'rgba(80,60,0,0.3)', hover: 'rgba(120,90,0,0.15)', hoverShadow: 'rgba(180,140,0,0.3)', shadow: '4px 0 16px rgba(0,0,0,0.9), -2px 0 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(0,0,0,0.5)' },
    right: { frame: 'linear-gradient(135deg, #1a0a0a 0%, #120606 50%, #0a0404 100%)', border: '#2a1010', borderBottom: '#1a0808', surface: 'linear-gradient(170deg, #180808 0%, #100404 30%, #0a0202 70%, #100404 100%)', panel: '#180808', panelBorder: '#2a1010', panelShadow: 'rgba(30,5,5,0.3)', hover: 'rgba(60,10,10,0.15)', hoverShadow: 'rgba(80,10,10,0.3)', shadow: '-4px 0 16px rgba(0,0,0,0.9), 2px 0 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(0,0,0,0.5)' } },
  // Left green (sickly), right red
  { left: { frame: 'linear-gradient(135deg, #0a2a0a 0%, #061a06 50%, #041204 100%)', border: '#104010', borderBottom: '#082808', surface: 'linear-gradient(170deg, #0d2a0d 0%, #081a08 30%, #060e06 70%, #081a08 100%)', panel: '#0d2a0d', panelBorder: '#104010', panelShadow: 'rgba(10,50,10,0.3)', hover: 'rgba(10,80,10,0.15)', hoverShadow: 'rgba(20,120,20,0.3)', shadow: '4px 0 16px rgba(0,0,0,0.9), -2px 0 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(0,0,0,0.5)' },
    right: { frame: 'linear-gradient(135deg, #5a0a0a 0%, #3d0606 50%, #2a0404 100%)', border: '#7a1010', borderBottom: '#5a0808', surface: 'linear-gradient(170deg, #4a0808 0%, #350606 30%, #280404 70%, #300606 100%)', panel: '#3d0606', panelBorder: '#5a0808', panelShadow: 'rgba(80,10,10,0.3)', hover: 'rgba(120,10,10,0.15)', hoverShadow: 'rgba(180,20,20,0.3)', shadow: '-4px 0 16px rgba(0,0,0,0.9), 2px 0 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(0,0,0,0.5)' } },
  // Left purple, right blue
  { left: { frame: 'linear-gradient(135deg, #2a0a3d 0%, #1a0628 50%, #100418 100%)', border: '#4a1060', borderBottom: '#300848', surface: 'linear-gradient(170deg, #220a3a 0%, #160626 30%, #0e0418 70%, #160626 100%)', panel: '#220a3a', panelBorder: '#3a0a50', panelShadow: 'rgba(50,10,80,0.3)', hover: 'rgba(80,10,120,0.15)', hoverShadow: 'rgba(120,20,180,0.3)', shadow: '4px 0 16px rgba(0,0,0,0.9), -2px 0 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(0,0,0,0.5)' },
    right: { frame: 'linear-gradient(135deg, #0a1a3d 0%, #060d2a 50%, #040818 100%)', border: '#102060', borderBottom: '#0a1848', surface: 'linear-gradient(170deg, #0d1e4a 0%, #081430 30%, #060e22 70%, #081430 100%)', panel: '#0d1e4a', panelBorder: '#102060', panelShadow: 'rgba(10,20,80,0.3)', hover: 'rgba(10,20,120,0.15)', hoverShadow: 'rgba(20,40,180,0.3)', shadow: '-4px 0 16px rgba(0,0,0,0.9), 2px 0 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(0,0,0,0.5)' } },
];

// Pick door color theme per level — mostly default (0), rarely others
function getDoorThemeIndex(levelIndex: number): number {
  // Seed based on level so it's consistent per level but varies
  const seed = (levelIndex * 7 + 3) % 17;
  if (seed === 5) return 1;   // both red — rare
  if (seed === 11) return 2;  // yellow + dark — rare
  if (seed === 14) return 3;  // green + red — rare
  if (seed === 8) return 4;   // purple + blue — rare
  return 0; // default red + blue
}

// Volume context — shared master gain
const masterGainRef = { current: 0.8 };

// Dark unsettling ambient sound — low drone + distant wind + subtle dissonance
function useAmbientSound() {
  const ctxRef = useRef<AudioContext | null>(null);
  const masterGainNodeRef = useRef<GainNode | null>(null);
  const nodesRef = useRef<AudioNode[]>([]);

  const start = useCallback(() => {
    try {
      const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new AudioContextClass();
      ctxRef.current = ctx;
      const nodes: AudioNode[] = [];

      // Master gain for volume control
      const masterGain = ctx.createGain();
      masterGain.gain.value = masterGainRef.current;
      masterGain.connect(ctx.destination);
      masterGainNodeRef.current = masterGain;

      // Layer 1: Deep sub-bass drone
      const osc1 = ctx.createOscillator();
      osc1.type = 'sawtooth';
      osc1.frequency.value = 28;
      const gain1 = ctx.createGain();
      gain1.gain.value = 0.07;
      const filter1 = ctx.createBiquadFilter();
      filter1.type = 'lowpass';
      filter1.frequency.value = 80;
      osc1.connect(filter1);
      filter1.connect(gain1);
      gain1.connect(masterGain);
      osc1.start();
      nodes.push(osc1, filter1, gain1);

      // Layer 2: Mid dissonant tone
      const osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.value = 55.5;
      const gain2 = ctx.createGain();
      gain2.gain.value = 0.04;
      osc2.connect(gain2);
      gain2.connect(masterGain);
      osc2.start();
      nodes.push(osc2, gain2);

      // Layer 3: Wind noise
      const bufferSize = ctx.sampleRate * 4;
      const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
      const noiseSource = ctx.createBufferSource();
      noiseSource.buffer = noiseBuffer;
      noiseSource.loop = true;
      const windFilter = ctx.createBiquadFilter();
      windFilter.type = 'bandpass';
      windFilter.frequency.value = 350;
      windFilter.Q.value = 0.3;
      const windGain = ctx.createGain();
      windGain.gain.value = 0.025;
      noiseSource.connect(windFilter);
      windFilter.connect(windGain);
      windGain.connect(masterGain);
      noiseSource.start();
      nodes.push(noiseSource, windFilter, windGain);

      // Layer 4: Slow LFO tremolo on drone for unease
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.12;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.03;
      lfo.connect(lfoGain);
      lfoGain.connect(gain1.gain);
      lfo.start();
      nodes.push(lfo, lfoGain);

      nodesRef.current = nodes;
    } catch {
      // Audio not available
    }
  }, []);

  const stop = useCallback(() => {
    try { ctxRef.current?.close(); } catch { /* ignore */ }
    ctxRef.current = null;
    masterGainNodeRef.current = null;
    nodesRef.current = [];
  }, []);

  const setVolume = useCallback((vol: number) => {
    masterGainRef.current = vol;
    if (masterGainNodeRef.current) {
      masterGainNodeRef.current.gain.value = vol;
    }
  }, []);

  return { start, stop, setVolume };
}

// Old door creaking sound — realistic slow eerie wooden creak
function playDoorCreak(ctx: AudioContext) {
  const now = ctx.currentTime;
  const vol = masterGainRef.current;

  const creakDuration = 2.2;
  const bufferSize = Math.floor(ctx.sampleRate * creakDuration);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const d = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    d[i] = Math.random() * 2 - 1;
  }

  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = buffer;

  const bp1 = ctx.createBiquadFilter();
  bp1.type = 'bandpass';
  bp1.frequency.setValueAtTime(320, now);
  bp1.frequency.linearRampToValueAtTime(160, now + creakDuration * 0.6);
  bp1.frequency.linearRampToValueAtTime(240, now + creakDuration);
  bp1.Q.value = 18;

  const bp2 = ctx.createBiquadFilter();
  bp2.type = 'bandpass';
  bp2.frequency.setValueAtTime(480, now);
  bp2.frequency.linearRampToValueAtTime(220, now + creakDuration);
  bp2.Q.value = 12;

  const merger = ctx.createGain();
  merger.gain.value = 1;

  const noiseSource2 = ctx.createBufferSource();
  noiseSource2.buffer = buffer;

  noiseSource.connect(bp1);
  noiseSource2.connect(bp2);
  bp1.connect(merger);
  bp2.connect(merger);

  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.0, now + 0.05);
  gainNode.gain.linearRampToValueAtTime(0.45 * vol, now + 0.3);
  gainNode.gain.setValueAtTime(0.45 * vol, now + 0.4);
  gainNode.gain.linearRampToValueAtTime(0.25 * vol, now + 0.9);
  gainNode.gain.linearRampToValueAtTime(0.35 * vol, now + 1.3);
  gainNode.gain.linearRampToValueAtTime(0.1 * vol, now + 1.8);
  gainNode.gain.linearRampToValueAtTime(0, now + creakDuration);

  const hipass = ctx.createBiquadFilter();
  hipass.type = 'highpass';
  hipass.frequency.value = 80;

  merger.connect(gainNode);
  gainNode.connect(hipass);
  hipass.connect(ctx.destination);

  noiseSource.start(now);
  noiseSource2.start(now);
  noiseSource.stop(now + creakDuration);
  noiseSource2.stop(now + creakDuration);

  const thudOsc = ctx.createOscillator();
  thudOsc.type = 'sine';
  thudOsc.frequency.setValueAtTime(55, now);
  thudOsc.frequency.exponentialRampToValueAtTime(28, now + 0.25);
  const thudGain = ctx.createGain();
  thudGain.gain.setValueAtTime(0.3 * vol, now);
  thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
  thudOsc.connect(thudGain);
  thudGain.connect(ctx.destination);
  thudOsc.start(now);
  thudOsc.stop(now + 0.3);
}

// Correct answer sound — satisfying bright chime/check sound
function playCorrectSound() {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioContextClass();
    const vol = masterGainRef.current;
    const now = ctx.currentTime;

    // Bright chime — two harmonious tones
    const freqs = [880, 1108.73]; // A5 + C#6 — major third, satisfying
    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now + i * 0.06);
      gain.gain.linearRampToValueAtTime(0.35 * vol, now + i * 0.06 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.06 + 0.5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.06);
      osc.stop(now + i * 0.06 + 0.55);
    });

    // Subtle sparkle overtone
    const sparkle = ctx.createOscillator();
    sparkle.type = 'triangle';
    sparkle.frequency.value = 2200;
    const sparkleGain = ctx.createGain();
    sparkleGain.gain.setValueAtTime(0.12 * vol, now + 0.05);
    sparkleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    sparkle.connect(sparkleGain);
    sparkleGain.connect(ctx.destination);
    sparkle.start(now + 0.05);
    sparkle.stop(now + 0.35);

    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1500);
  } catch {
    // Audio not available
  }
}

// Scary game over sound — deep cinematic brass + corrupted toys + distorted human
export function playGameOverSound() {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioContextClass();
    const vol = masterGainRef.current;

    // Layer 1: Deep cinematic brass hit
    const brassOsc = ctx.createOscillator();
    brassOsc.type = 'sawtooth';
    brassOsc.frequency.setValueAtTime(55, ctx.currentTime);
    brassOsc.frequency.exponentialRampToValueAtTime(28, ctx.currentTime + 2.5);
    const brassGain = ctx.createGain();
    brassGain.gain.setValueAtTime(0.9 * vol, ctx.currentTime);
    brassGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 2.8);
    const brassFilter = ctx.createBiquadFilter();
    brassFilter.type = 'lowpass';
    brassFilter.frequency.value = 400;
    brassOsc.connect(brassFilter);
    brassFilter.connect(brassGain);
    brassGain.connect(ctx.destination);
    brassOsc.start(ctx.currentTime);
    brassOsc.stop(ctx.currentTime + 2.8);

    // Layer 2: Second brass hit (dissonant interval)
    const brass2 = ctx.createOscillator();
    brass2.type = 'sawtooth';
    brass2.frequency.setValueAtTime(73.4, ctx.currentTime + 0.05);
    brass2.frequency.exponentialRampToValueAtTime(36, ctx.currentTime + 2.5);
    const brass2Gain = ctx.createGain();
    brass2Gain.gain.setValueAtTime(0.7 * vol, ctx.currentTime + 0.05);
    brass2Gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 2.8);
    brass2.connect(brass2Gain);
    brass2Gain.connect(ctx.destination);
    brass2.start(ctx.currentTime + 0.05);
    brass2.stop(ctx.currentTime + 2.8);

    // Layer 3: Corrupted music box / toy (high pitched, glitchy)
    const toyOsc = ctx.createOscillator();
    toyOsc.type = 'square';
    toyOsc.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
    toyOsc.frequency.setValueAtTime(440, ctx.currentTime + 0.25);
    toyOsc.frequency.setValueAtTime(660, ctx.currentTime + 0.4);
    toyOsc.frequency.setValueAtTime(220, ctx.currentTime + 0.55);
    toyOsc.frequency.setValueAtTime(550, ctx.currentTime + 0.7);
    const toyGain = ctx.createGain();
    toyGain.gain.setValueAtTime(0, ctx.currentTime);
    toyGain.gain.setValueAtTime(0.18 * vol, ctx.currentTime + 0.1);
    toyGain.gain.setValueAtTime(0.05 * vol, ctx.currentTime + 0.3);
    toyGain.gain.setValueAtTime(0.15 * vol, ctx.currentTime + 0.5);
    toyGain.gain.setValueAtTime(0, ctx.currentTime + 0.9);
    const toyFilter = ctx.createBiquadFilter();
    toyFilter.type = 'bandpass';
    toyFilter.frequency.value = 1200;
    toyFilter.Q.value = 3;
    toyOsc.connect(toyFilter);
    toyFilter.connect(toyGain);
    toyGain.connect(ctx.destination);
    toyOsc.start(ctx.currentTime + 0.1);
    toyOsc.stop(ctx.currentTime + 1.0);

    // Layer 4: Distorted human scream (noise burst shaped like a scream)
    const screamBuffer = ctx.createBuffer(1, ctx.sampleRate * 1.5, ctx.sampleRate);
    const screamData = screamBuffer.getChannelData(0);
    for (let i = 0; i < screamData.length; i++) {
      const t = i / ctx.sampleRate;
      screamData[i] = (Math.random() * 2 - 1) * Math.sin(Math.PI * t / 1.5) * Math.sin(t * 18);
    }
    const screamSource = ctx.createBufferSource();
    screamSource.buffer = screamBuffer;
    const screamFilter = ctx.createBiquadFilter();
    screamFilter.type = 'bandpass';
    screamFilter.frequency.value = 1800;
    screamFilter.Q.value = 1.2;
    const screamGain = ctx.createGain();
    screamGain.gain.setValueAtTime(0, ctx.currentTime + 0.3);
    screamGain.gain.linearRampToValueAtTime(0.6 * vol, ctx.currentTime + 0.5);
    screamGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.8);
    const distortion = ctx.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
      const x = (i * 2) / 256 - 1;
      curve[i] = (Math.PI + 400) * x / (Math.PI + 400 * Math.abs(x));
    }
    distortion.curve = curve;
    screamSource.connect(screamFilter);
    screamFilter.connect(distortion);
    distortion.connect(screamGain);
    screamGain.connect(ctx.destination);
    screamSource.start(ctx.currentTime + 0.3);

    // Layer 5: Sub-bass impact
    const subOsc = ctx.createOscillator();
    subOsc.type = 'sine';
    subOsc.frequency.setValueAtTime(80, ctx.currentTime);
    subOsc.frequency.exponentialRampToValueAtTime(20, ctx.currentTime + 0.4);
    const subGain = ctx.createGain();
    subGain.gain.setValueAtTime(1.2 * vol, ctx.currentTime);
    subGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    subOsc.connect(subGain);
    subGain.connect(ctx.destination);
    subOsc.start(ctx.currentTime);
    subOsc.stop(ctx.currentTime + 0.5);

    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 4000);
  } catch {
    // Audio not available
  }
}

// Aggressive flickering light hook
function useFlickerLight() {
  const [lightOn, setLightOn] = useState(true);
  const [intensity, setIntensity] = useState(1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let running = true;

    const flicker = () => {
      if (!running) return;

      const rand = Math.random();

      if (rand < 0.15) {
        const count = 3 + Math.floor(Math.random() * 4);
        let i = 0;
        const stutter = () => {
          if (!running || i >= count * 2) {
            setLightOn(true);
            setIntensity(0.7 + Math.random() * 0.3);
            timerRef.current = setTimeout(flicker, 80 + Math.random() * 200);
            return;
          }
          setLightOn(i % 2 === 0);
          setIntensity(0.3 + Math.random() * 0.7);
          i++;
          timerRef.current = setTimeout(stutter, 30 + Math.random() * 60);
        };
        stutter();
      } else if (rand < 0.3) {
        setLightOn(false);
        setIntensity(0);
        timerRef.current = setTimeout(() => {
          if (!running) return;
          setLightOn(true);
          setIntensity(0.5 + Math.random() * 0.5);
          timerRef.current = setTimeout(flicker, 100 + Math.random() * 300);
        }, 300 + Math.random() * 700);
      } else if (rand < 0.55) {
        setIntensity(0.15 + Math.random() * 0.4);
        setLightOn(true);
        timerRef.current = setTimeout(flicker, 60 + Math.random() * 120);
      } else {
        setLightOn(true);
        setIntensity(0.75 + Math.random() * 0.25);
        timerRef.current = setTimeout(flicker, 150 + Math.random() * 600);
      }
    };

    timerRef.current = setTimeout(flicker, 400);
    return () => {
      running = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { lightOn, intensity };
}

export default function GameScreen({ onRestart }: GameScreenProps) {
  const [state, setState] = useState<GameState>(() => ({
    phase: 'doors',
    currentRoomIndex: 0,
    selectedDoor: null,
    shakeScreen: false,
    zoomingDoor: null,
    rooms: buildRoomSequence(),
  }));

  const [volume, setVolumeState] = useState(0.8);
  const [showVolume, setShowVolume] = useState(false);
  const [correctActionKey, setCorrectActionKey] = useState<string | null>(null);

  const ambient = useAmbientSound();
  const { lightOn, intensity } = useFlickerLight();
  const audioCtxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    ambient.start();
    return () => ambient.stop();
  }, []);

  const handleVolumeChange = useCallback((val: number) => {
    setVolumeState(val);
    ambient.setVolume(val);
  }, [ambient]);

  // Create/reuse AudioContext for door creak
  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      audioCtxRef.current = new AudioContextClass();
    }
    return audioCtxRef.current;
  }, []);

  const currentRoom = state.rooms[state.currentRoomIndex];
  const totalRooms = state.rooms.length;
  const progressPercent = (state.currentRoomIndex / totalRooms) * 100;

  const leftInscription = DOOR_INSCRIPTIONS[state.currentRoomIndex * 2 % DOOR_INSCRIPTIONS.length];
  const rightInscription = DOOR_INSCRIPTIONS[(state.currentRoomIndex * 2 + 1) % DOOR_INSCRIPTIONS.length];
  const doorTheme = DOOR_COLOR_THEMES[getDoorThemeIndex(state.currentRoomIndex)];

  const handleDoorClick = useCallback((door: 1 | 2) => {
    if (state.zoomingDoor !== null) return;
    try {
      const ctx = getAudioCtx();
      playDoorCreak(ctx);
    } catch { /* ignore */ }
    setState((prev) => ({ ...prev, zoomingDoor: door, phase: 'zoom' }));
    setTimeout(() => {
      setState((prev) => ({ ...prev, selectedDoor: door, phase: 'room', zoomingDoor: null }));
    }, 900);
  }, [state.zoomingDoor, getAudioCtx]);

  const handleActionClick = useCallback((actionKey: string) => {
    const isVictoryRoom = currentRoom.isVictory;

    if (isVictoryRoom) {
      setState((prev) => ({ ...prev, phase: 'victory' }));
      return;
    }

    const isCorrect = actionKey === currentRoom.correctAction;

    if (isCorrect) {
      // Play correct sound and show flash
      playCorrectSound();
      setCorrectActionKey(actionKey);
      setTimeout(() => {
        setCorrectActionKey(null);
        const nextIndex = state.currentRoomIndex + 1;
        if (nextIndex >= state.rooms.length) {
          setState((prev) => ({ ...prev, phase: 'victory' }));
        } else {
          setState((prev) => ({
            ...prev,
            phase: 'doors',
            currentRoomIndex: nextIndex,
            selectedDoor: null,
            zoomingDoor: null,
          }));
        }
      }, 600);
    } else {
      playGameOverSound();
      setState((prev) => ({ ...prev, phase: 'jumpscare', shakeScreen: true }));
      setTimeout(() => {
        setState((prev) => ({ ...prev, shakeScreen: false }));
      }, 600);
    }
  }, [currentRoom, state.currentRoomIndex, state.rooms.length]);

  const handleRestartFromJumpscare = useCallback(() => {
    ambient.stop();
    onRestart();
  }, [onRestart, ambient]);

  if (state.phase === 'victory') {
    return <VictoryScreen onPlayAgain={() => { ambient.stop(); onRestart(); }} />;
  }

  if (state.phase === 'jumpscare') {
    return (
      <JumpscareOverlay
        room={currentRoom}
        onRestart={handleRestartFromJumpscare}
      />
    );
  }

  const lightOpacity = lightOn ? intensity : 0;
  const lightColor = `rgba(${Math.floor(180 + intensity * 40)}, ${Math.floor(160 + intensity * 30)}, ${Math.floor(100 + intensity * 20)}, ${lightOpacity})`;
  const lightGlow = lightOn ? `0 0 ${40 + intensity * 60}px ${20 + intensity * 40}px rgba(200,185,120,${lightOpacity * 0.4}), 0 0 ${80 + intensity * 80}px ${40 + intensity * 60}px rgba(200,185,120,${lightOpacity * 0.15})` : 'none';

  return (
    <div
      className={`relative min-h-screen bg-horror flex flex-col overflow-hidden ${state.shakeScreen ? 'shake-screen' : ''}`}
    >
      {/* Scanlines */}
      <div className="scanlines absolute inset-0 z-10 pointer-events-none" aria-hidden="true" />
      {/* Vignette */}
      <div className="candle-vignette absolute inset-0 z-10 pointer-events-none" aria-hidden="true" />
      {/* Cobwebs */}
      <CobwebSvg className="cobweb-tl z-20" />
      <CobwebSvg className="cobweb-tr z-20" />
      {/* Blood drips */}
      <BloodDrips />
      {/* Fog */}
      <div className="fog-layer z-10" aria-hidden="true" />

      {/* Volume control button — fixed top right */}
      <div className="fixed top-3 right-3 z-50">
        <button
          onClick={() => setShowVolume((v) => !v)}
          className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer"
          style={{
            background: 'rgba(20,0,0,0.85)',
            border: '1px solid rgba(204,0,0,0.5)',
            color: '#f5e6e6',
            fontSize: '18px',
            backdropFilter: 'blur(4px)',
          }}
          aria-label="Garso nustatymai"
        >
          {volume === 0 ? '🔇' : volume < 0.4 ? '🔈' : volume < 0.75 ? '🔉' : '🔊'}
        </button>
        {showVolume && (
          <div
            className="absolute top-12 right-0 rounded-xl p-3 flex flex-col items-center gap-2"
            style={{
              background: 'rgba(15,0,0,0.95)',
              border: '1px solid rgba(204,0,0,0.4)',
              backdropFilter: 'blur(8px)',
              minWidth: '140px',
            }}
          >
            <span className="font-sans text-xs text-muted-foreground tracking-widest uppercase">Garsumas</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
              className="volume-slider w-full"
              aria-label="Garso lygis"
            />
            <span className="font-sans text-xs text-primary font-semibold">
              {Math.round(volume * 100)}%
            </span>
          </div>
        )}
      </div>

      <div className="relative z-30 flex flex-col min-h-screen px-3 sm:px-4 py-4 sm:py-5 max-w-lg mx-auto w-full">

        {/* Progress header */}
        <header className="mb-4 sm:mb-5 fade-in-horror">
          <div className="flex items-center justify-between mb-2">
            <div className="room-badge px-3 py-1 rounded-full">
              <span className="font-sans text-primary text-sm tracking-widest font-semibold">
                {state.currentRoomIndex + 1} / {totalRooms}
              </span>
            </div>
            <div className="flex gap-1 flex-wrap justify-end max-w-[180px] sm:max-w-[200px]" aria-label="Kambarių progresas">
              {state.rooms.map((_, idx) => (
                <div
                  key={`progress-dot-${idx}`}
                  className={`w-2 h-2 rounded-full transition-all duration-500 ${
                    idx < state.currentRoomIndex
                      ? 'bg-primary opacity-100'
                      : idx === state.currentRoomIndex
                      ? 'bg-primary opacity-100 pulse-blood-fast' : 'bg-border opacity-40'
                  }`}
                  title={`Kambarys ${idx + 1}`}
                />
              ))}
            </div>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="progress-blood h-full rounded-full"
              style={{ width: `${progressPercent}%` }}
              role="progressbar"
              aria-valuenow={state.currentRoomIndex}
              aria-valuemin={0}
              aria-valuemax={totalRooms}
            />
          </div>
        </header>

        {/* PHASE: DOORS */}
        {(state.phase === 'doors' || state.phase === 'zoom') && (
          <div className="flex flex-col items-center justify-center flex-1 fade-in-horror">
            <p className="font-sans text-muted-foreground text-sm tracking-widest uppercase mb-1 flicker-text">
              Priešais tave stovi dvi durys...
            </p>
            <p className="font-sans text-primary text-base tracking-widest mb-4 flicker-text font-semibold">
              Rinkis :)
            </p>

            {/* Hallway scene with doors */}
            <div
              className="relative w-full rounded-xl overflow-hidden"
              style={{
                height: 'min(420px, 58vw)',
                minHeight: '280px',
                background: 'linear-gradient(180deg, #050505 0%, #0d0a06 30%, #141008 60%, #0a0a0a 100%)',
              }}
            >
              {/* Ceiling */}
              <div
                className="absolute top-0 left-0 right-0"
                style={{
                  height: '18%',
                  background: 'linear-gradient(180deg, #060606 0%, #111111 100%)',
                }}
              />

              {/* Flickering ceiling light */}
              <div
                className="absolute"
                style={{
                  top: '4%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '8px',
                  background: lightOn
                    ? `linear-gradient(90deg, transparent, ${lightColor}, transparent)`
                    : 'transparent',
                  boxShadow: lightGlow,
                  borderRadius: '2px',
                  transition: 'none',
                }}
              />

              {/* Light cone from ceiling */}
              <div
                className="absolute"
                style={{
                  top: '12%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '0',
                  height: '0',
                  borderLeft: '120px solid transparent',
                  borderRight: '120px solid transparent',
                  borderTop: `200px solid rgba(200,185,120,${lightOpacity * 0.06})`,
                  pointerEvents: 'none',
                  transition: 'none',
                }}
              />

              {/* Floor */}
              <div
                className="absolute bottom-0 left-0 right-0"
                style={{
                  height: '15%',
                  background: 'linear-gradient(180deg, #1a1510 0%, #0a0906 100%)',
                }}
              />

              {/* Wall */}
              <div
                className="absolute"
                style={{
                  top: '18%',
                  bottom: '15%',
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(180deg, #1e1a14 0%, #161210 50%, #100e0c 100%)',
                }}
              />

              {/* Dark vignette edges */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.9) 100%)',
                }}
              />

              {/* LEFT DOOR */}
              <button
                onClick={() => handleDoorClick(1)}
                disabled={state.phase === 'zoom'}
                className="absolute cursor-pointer group"
                style={{
                  left: '2%',
                  top: '10%',
                  width: '38%',
                  height: '82%',
                  transform: state.zoomingDoor === 1 ? 'scale(2.8)' : 'scale(1)',
                  transformOrigin: 'center center',
                  transition: 'transform 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  zIndex: state.zoomingDoor === 1 ? 50 : 10,
                  opacity: state.zoomingDoor === 2 ? 0 : 1,
                  touchAction: 'manipulation',
                }}
                aria-label="Kairiosios durys"
              >
                {/* Door frame / architrave */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: doorTheme.left.frame,
                    border: `5px solid ${doorTheme.left.border}`,
                    borderBottom: `8px solid ${doorTheme.left.borderBottom}`,
                    boxShadow: doorTheme.left.shadow,
                  }}
                >
                  {/* Door surface */}
                  <div
                    className="absolute"
                    style={{
                      inset: '6px',
                      background: doorTheme.left.surface,
                      boxShadow: 'inset 3px 3px 12px rgba(0,0,0,0.6), inset -2px -2px 8px rgba(0,0,0,0.4)',
                    }}
                  >
                    {/* Wood grain lines */}
                    {[15, 28, 42, 58, 72, 85].map((pct) => (
                      <div key={`lg-${pct}`} className="absolute" style={{ top: 0, bottom: 0, left: `${pct}%`, width: '1px', background: 'rgba(0,0,0,0.3)', opacity: 0.6 }} />
                    ))}

                    {/* INSCRIPTION — eye level, larger and readable */}
                    <div className="absolute pointer-events-none" style={{ top: '10%', left: '5%', right: '5%', height: '28%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span
                        style={{
                          fontFamily: '"Oswald", sans-serif',
                          fontSize: 'clamp(10px, 2.8vw, 16px)',
                          color: '#000000',
                          textShadow: '0 1px 0 rgba(255,255,255,0.2)',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          lineHeight: 1.2,
                          textAlign: 'center',
                          wordBreak: 'break-word',
                          textTransform: 'uppercase',
                          maxWidth: '100%',
                          display: 'block',
                        }}
                      >
                        {leftInscription}
                      </span>
                    </div>

                    {/* Top panel */}
                    <div className="absolute" style={{
                      top: '40%', left: '8%', right: '8%', height: '18%',
                      background: `linear-gradient(160deg, ${doorTheme.left.panel} 0%, rgba(0,0,0,0.4) 100%)`,
                      border: `2px solid ${doorTheme.left.panelBorder}`,
                      boxShadow: `inset 1px 1px 4px rgba(0,0,0,0.5), 1px 1px 3px ${doorTheme.left.panelShadow}`,
                    }}>
                      <div className="absolute inset-1" style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '1px' }} />
                    </div>

                    {/* Bottom large panel */}
                    <div className="absolute" style={{
                      top: '62%', left: '8%', right: '8%', height: '28%',
                      background: `linear-gradient(160deg, ${doorTheme.left.panel} 0%, rgba(0,0,0,0.4) 100%)`,
                      border: `2px solid ${doorTheme.left.panelBorder}`,
                      boxShadow: 'inset 1px 1px 4px rgba(0,0,0,0.5)',
                    }}>
                      <div className="absolute inset-1" style={{ background: 'rgba(0,0,0,0.2)' }} />
                    </div>

                    {/* Doorknob — right side, at 55% height */}
                    <div className="absolute" style={{
                      right: '10%', top: '55%', transform: 'translateY(-50%)',
                      width: '14px', height: '14px', borderRadius: '50%',
                      background: 'radial-gradient(circle at 35% 35%, #c8960e, #7a5a08)',
                      boxShadow: '0 0 6px rgba(180,130,10,0.5), inset 0 1px 2px rgba(255,200,50,0.3)',
                    }} />
                    {/* Keyhole */}
                    <div className="absolute" style={{
                      right: '10%', top: '62%',
                      width: '6px', height: '10px',
                      background: '#0a0604',
                      borderRadius: '3px 3px 0 0',
                      boxShadow: '0 0 3px rgba(0,0,0,0.8)',
                    }} />

                    {/* Hinges */}
                    <div className="absolute" style={{ left: '4%', top: '12%', width: '8px', height: '20px', background: 'linear-gradient(90deg, #5a4020, #3a2810)', borderRadius: '2px', boxShadow: '1px 0 3px rgba(0,0,0,0.6)' }} />
                    <div className="absolute" style={{ left: '4%', top: '45%', width: '8px', height: '20px', background: 'linear-gradient(90deg, #5a4020, #3a2810)', borderRadius: '2px', boxShadow: '1px 0 3px rgba(0,0,0,0.6)' }} />
                    <div className="absolute" style={{ left: '4%', top: '75%', width: '8px', height: '20px', background: 'linear-gradient(90deg, #5a4020, #3a2810)', borderRadius: '2px', boxShadow: '1px 0 3px rgba(0,0,0,0.6)' }} />

                    {/* Hover glow */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ background: doorTheme.left.hover, boxShadow: `inset 0 0 25px ${doorTheme.left.hoverShadow}` }}
                    />
                  </div>
                </div>
              </button>

              {/* RIGHT DOOR */}
              <button
                onClick={() => handleDoorClick(2)}
                disabled={state.phase === 'zoom'}
                className="absolute cursor-pointer group"
                style={{
                  right: '2%',
                  top: '10%',
                  width: '38%',
                  height: '82%',
                  transform: state.zoomingDoor === 2 ? 'scale(2.8)' : 'scale(1)',
                  transformOrigin: 'center center',
                  transition: 'transform 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  zIndex: state.zoomingDoor === 2 ? 50 : 10,
                  opacity: state.zoomingDoor === 1 ? 0 : 1,
                  touchAction: 'manipulation',
                }}
                aria-label="Dešiniosios durys"
              >
                {/* Door frame / architrave */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: doorTheme.right.frame,
                    border: `5px solid ${doorTheme.right.border}`,
                    borderBottom: `8px solid ${doorTheme.right.borderBottom}`,
                    boxShadow: doorTheme.right.shadow,
                  }}
                >
                  {/* Door surface */}
                  <div
                    className="absolute"
                    style={{
                      inset: '6px',
                      background: doorTheme.right.surface,
                      boxShadow: 'inset 3px 3px 12px rgba(0,0,0,0.6), inset -2px -2px 8px rgba(0,0,0,0.4)',
                    }}
                  >
                    {/* Wood grain lines */}
                    {[15, 28, 42, 58, 72, 85].map((pct) => (
                      <div key={`rg-${pct}`} className="absolute" style={{ top: 0, bottom: 0, left: `${pct}%`, width: '1px', background: 'rgba(0,0,0,0.3)', opacity: 0.6 }} />
                    ))}

                    {/* INSCRIPTION — eye level, larger and readable */}
                    <div className="absolute pointer-events-none" style={{ top: '10%', left: '5%', right: '5%', height: '28%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span
                        style={{
                          fontFamily: '"Oswald", sans-serif',
                          fontSize: 'clamp(10px, 2.8vw, 16px)',
                          color: '#000000',
                          textShadow: '0 1px 0 rgba(255,255,255,0.2)',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          lineHeight: 1.2,
                          textAlign: 'center',
                          wordBreak: 'break-word',
                          textTransform: 'uppercase',
                          maxWidth: '100%',
                          display: 'block',
                        }}
                      >
                        {rightInscription}
                      </span>
                    </div>

                    {/* Top panel */}
                    <div className="absolute" style={{
                      top: '40%', left: '8%', right: '8%', height: '18%',
                      background: `linear-gradient(160deg, ${doorTheme.right.panel} 0%, rgba(0,0,0,0.4) 100%)`,
                      border: `2px solid ${doorTheme.right.panelBorder}`,
                      boxShadow: `inset 1px 1px 4px rgba(0,0,0,0.5), 1px 1px 3px ${doorTheme.right.panelShadow}`,
                    }}>
                      <div className="absolute inset-1" style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '1px' }} />
                    </div>

                    {/* Bottom large panel */}
                    <div className="absolute" style={{
                      top: '62%', left: '8%', right: '8%', height: '28%',
                      background: `linear-gradient(160deg, ${doorTheme.right.panel} 0%, rgba(0,0,0,0.4) 100%)`,
                      border: `2px solid ${doorTheme.right.panelBorder}`,
                      boxShadow: 'inset 1px 1px 4px rgba(0,0,0,0.5)',
                    }}>
                      <div className="absolute inset-1" style={{ background: 'rgba(0,0,0,0.2)' }} />
                    </div>

                    {/* Doorknob — left side, at 55% height */}
                    <div className="absolute" style={{
                      left: '10%', top: '55%', transform: 'translateY(-50%)',
                      width: '14px', height: '14px', borderRadius: '50%',
                      background: 'radial-gradient(circle at 35% 35%, #c8960e, #7a5a08)',
                      boxShadow: '0 0 6px rgba(180,130,10,0.5), inset 0 1px 2px rgba(255,200,50,0.3)',
                    }} />
                    {/* Keyhole */}
                    <div className="absolute" style={{
                      left: '10%', top: '62%',
                      width: '6px', height: '10px',
                      background: '#0a0604',
                      borderRadius: '3px 3px 0 0',
                      boxShadow: '0 0 3px rgba(0,0,0,0.8)',
                    }} />

                    {/* Hinges */}
                    <div className="absolute" style={{ right: '4%', top: '12%', width: '8px', height: '20px', background: 'linear-gradient(90deg, #3a2810, #5a4020)', borderRadius: '2px', boxShadow: '-1px 0 3px rgba(0,0,0,0.6)' }} />
                    <div className="absolute" style={{ right: '4%', top: '45%', width: '8px', height: '20px', background: 'linear-gradient(90deg, #3a2810, #5a4020)', borderRadius: '2px', boxShadow: '-1px 0 3px rgba(0,0,0,0.6)' }} />
                    <div className="absolute" style={{ right: '4%', top: '75%', width: '8px', height: '20px', background: 'linear-gradient(90deg, #3a2810, #5a4020)', borderRadius: '2px', boxShadow: '-1px 0 3px rgba(0,0,0,0.6)' }} />

                    {/* Hover glow */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ background: doorTheme.right.hover, boxShadow: `inset 0 0 25px ${doorTheme.right.hoverShadow}` }}
                    />
                  </div>
                </div>
              </button>

              {/* Zoom overlay flash when zooming */}
              {state.phase === 'zoom' && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'rgba(0,0,0,0)',
                    animation: 'zoomFadeBlack 0.9s ease-in forwards',
                    zIndex: 60,
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* PHASE: ROOM */}
        {state.phase === 'room' && (
          <div className="fixed inset-0 flex flex-col slide-up-horror" style={{ zIndex: 40 }}>
            {/* Full-page background image */}
            {currentRoom.roomImageUrl ? (
              <img
                src={currentRoom.roomImageUrl}
                alt={currentRoom.roomImageAlt}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: 'brightness(0.55) saturate(0.7) contrast(1.1)' }}
              />
            ) : (
              <div className="absolute inset-0 bg-black" />
            )}
            {/* Dark overlay gradient */}
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0.85) 100%)' }}
            />
            {/* Scanlines */}
            <div className="scanlines absolute inset-0 pointer-events-none opacity-30" aria-hidden="true" />

            {/* Content panel — overlaid at bottom */}
            <div className="relative z-10 flex flex-col justify-end h-full px-3 sm:px-4 pb-4 sm:pb-6 pt-4 max-w-lg mx-auto w-full">
              {/* Room name + atmosphere */}
              <div className="mb-3 sm:mb-4">
                <h2
                  className="font-sans text-xl sm:text-2xl md:text-3xl text-white tracking-wide font-bold"
                  style={{ textShadow: '0 0 20px rgba(204,0,0,0.9), 0 2px 8px rgba(0,0,0,0.9)' }}
                >
                  {currentRoom.name}
                </h2>
                <p className="font-sans text-muted-foreground text-xs tracking-widest uppercase mt-0.5">
                  {currentRoom.atmosphere}
                </p>
              </div>

              {/* Room card */}
              <div className="room-card rounded-xl p-3 sm:p-4 md:p-5 flex flex-col" style={{ background: 'rgba(10,5,5,0.88)', backdropFilter: 'blur(6px)' }}>
                <p className="font-sans text-foreground text-sm sm:text-base md:text-lg leading-relaxed text-center mb-4">
                  {currentRoom.description}
                </p>

                <div className="flex items-center gap-2 mb-3" aria-hidden="true">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent to-border" />
                  <span className="text-primary text-sm">🩸</span>
                  <div className="h-px flex-1 bg-gradient-to-l from-transparent to-border" />
                </div>

                <p className="font-sans text-muted-foreground text-xs sm:text-sm text-center mb-3 tracking-widest uppercase">
                  Ką darai?
                </p>

                {/* 2x2 action grid with emojis */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {ACTIONS.map((action) => (
                    <button
                      key={`action-${action.key}`}
                      onClick={() => handleActionClick(action.key)}
                      className={`action-btn rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center gap-1 cursor-pointer ${correctActionKey === action.key ? 'action-btn-correct' : ''}`}
                      aria-label={action.label}
                      style={{ touchAction: 'manipulation', minHeight: '72px' }}
                    >
                      <span className="text-xl sm:text-2xl" aria-hidden="true">{action.emoji}</span>
                      <span className="font-sans text-xs sm:text-sm font-semibold text-foreground text-center leading-tight">
                        {action.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}